import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, ShoppingBag, Heart, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";

const DEFAULT_NAV = [
  { label: "HOME", url: "/" }, { label: "SHOP", url: "/shop" },
  { label: "OUR STORY", url: "/our-story" }, { label: "SUSTAINABILITY", url: "/#deadstock" },
  { label: "LOOKBOOK", url: "/shop" }, { label: "JOURNAL", url: "/#process" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const { user } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = settings?.navigation?.header?.length ? settings.navigation.header : DEFAULT_NAV;
  const announcement = settings?.site?.announcement || "COMPLIMENTARY SHIPPING ON ORDERS OVER $200 · EVERY PIECE RESCUED";
  const left = links.slice(0, Math.ceil(links.length / 2));
  const right = links.slice(Math.ceil(links.length / 2));

  const NavLink = ({ l }) => (
    <a href={l.url} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
       className="font-body uppercase tracking-wide2 text-[0.7rem] text-ink/80 hover:text-ink transition-colors duration-300">
      {l.label}
    </a>
  );

  return (
    <header className="sticky top-0 z-40">
      <div className="bg-ink text-linen text-center py-2 px-4">
        <p data-testid="announcement-bar" className="font-body uppercase tracking-luxe text-[0.6rem] md:text-[0.65rem]">{announcement}</p>
      </div>
      <div className="bg-linen/85 backdrop-blur-md border-b hairline">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8 h-20 flex items-center gap-4">
          <div className="flex-1 flex items-center min-w-0">
            <nav className="hidden xl:flex items-center gap-5">{left.map((l, i) => <NavLink key={i} l={l} />)}</nav>
            <button className="xl:hidden icon-thin" data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(true)}><Menu /></button>
          </div>
          <div className="shrink-0 flex justify-center"><Logo /></div>
          <div className="flex-1 flex items-center justify-end gap-4 min-w-0">
            <nav className="hidden xl:flex items-center gap-5">{right.map((l, i) => <NavLink key={i} l={l} />)}</nav>
            <Link to={user ? (["super_admin","store_manager","fulfillment_staff"].includes(user.role) ? "/admin" : user.role === "affiliate" ? "/affiliate/dashboard" : "/account") : "/login"} data-testid="header-account-link" className="hidden lg:inline font-body uppercase tracking-wide2 text-[0.58rem] text-ink/60 hover:text-ink transition-colors">ACCOUNT</Link>
            <button onClick={() => nav("/shop")} className="icon-thin hover:opacity-60 transition-opacity" data-testid="search-btn"><Search size={19} /></button>
            <Link to={user ? (["super_admin","store_manager","fulfillment_staff"].includes(user.role) ? "/admin" : user.role === "affiliate" ? "/affiliate/dashboard" : "/account") : "/login"}
                  className="icon-thin hover:opacity-60 transition-opacity" data-testid="account-btn"><User size={19} /></Link>
            <Link to="/account?tab=wishlist" className="relative icon-thin hover:opacity-60 transition-opacity" data-testid="wishlist-btn">
              <Heart size={19} />
              {wishCount > 0 && <span className="absolute -top-2 -right-2 bg-ink text-linen text-[0.55rem] w-4 h-4 flex items-center justify-center rounded-full">{wishCount}</span>}
            </Link>
            <button onClick={() => setOpen(true)} className="relative icon-thin hover:opacity-60 transition-opacity" data-testid="cart-open-btn">
              <ShoppingBag size={19} />
              <span className="absolute -top-2 -right-2 bg-ink text-linen text-[0.55rem] w-4 h-4 flex items-center justify-center rounded-full" data-testid="cart-count">{count}</span>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-linen p-6 xl:hidden" data-testid="mobile-menu">
          <div className="flex justify-between items-center mb-10"><Logo /><button onClick={() => setMobileOpen(false)} className="icon-thin"><X /></button></div>
          <nav className="flex flex-col gap-6">
            {links.map((l, i) => (
              <a key={i} href={l.url} onClick={() => setMobileOpen(false)} className="font-heading text-3xl">{l.label}</a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
